import { useState, useEffect } from "react";
import { generatePassword, passwordStrength, strengthLabel } from "../lib/passwordUtils";
import { useLang } from "../lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Eye, EyeOff, Dices, Save } from "lucide-react";

export default function PasswordModal({ open, onClose, onSave, entry }) {
  var { t } = useLang();
  var [title, setTitle] = useState("");
  var [username, setUsername] = useState("");
  var [password, setPassword] = useState("");
  var [url, setUrl] = useState("");
  var [category, setCategory] = useState("web");
  var [notes, setNotes] = useState("");
  var [showPwd, setShowPwd] = useState(false);
  var [saving, setSaving] = useState(false);

  useEffect(function() {
    if (open) {
      if (entry) { setTitle(entry.title||""); setUsername(entry.username||""); setPassword(entry.password||""); setUrl(entry.url||""); setCategory(entry.category||"web"); setNotes(entry.notes||""); }
      else { setTitle(""); setUsername(""); setPassword(""); setUrl(""); setCategory("web"); setNotes(""); }
      setShowPwd(false);
    }
  }, [open, entry]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim() || !password.trim()) return;
    setSaving(true);
    await onSave({ title, username, password, url, category, notes }, entry?.id);
    setSaving(false);
  }

  var strength = passwordStrength(password);
  var sInfo = strengthLabel(strength);

  return (
    <Dialog open={open} onOpenChange={function(o){if(!o) onClose();}}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-lg" data-testid="password-modal">
        <DialogHeader>
          <DialogTitle data-testid="modal-title">{entry ? t.editEntry : t.newEntry}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.title} *</Label>
              <Input data-testid="field-title" value={title} onChange={function(e){setTitle(e.target.value)}} placeholder="Gmail, Amazon..." className="bg-slate-50 border-slate-200 text-slate-800" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.category}</Label>
              <select data-testid="field-category" value={category} onChange={function(e){setCategory(e.target.value)}} className="w-full h-9 px-3 rounded-md bg-slate-50 border border-slate-200 text-slate-700 text-sm outline-none">
                <option value="web">Web</option><option value="social">Social</option><option value="banking">Banking</option><option value="email">Email</option><option value="work">Lavoro</option><option value="altro">Altro</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.username}</Label>
              <Input data-testid="field-username" value={username} onChange={function(e){setUsername(e.target.value)}} className="bg-slate-50 border-slate-200 text-slate-800" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.website}</Label>
              <Input data-testid="field-url" value={url} onChange={function(e){setUrl(e.target.value)}} className="bg-slate-50 border-slate-200 text-slate-800" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500">{t.password} *</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input data-testid="field-password" type={showPwd?"text":"password"} value={password} onChange={function(e){setPassword(e.target.value)}} className="bg-slate-50 border-slate-200 text-slate-800 pr-10" required />
                <button type="button" onClick={function(){setShowPwd(!showPwd)}} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button type="button" data-testid="generate-password-button" variant="outline" onClick={function(){setPassword(generatePassword(20)); setShowPwd(true);}} className="bg-slate-50 border-slate-200 text-slate-600 hover:bg-green-50 hover:text-green-700 shrink-0">
                <Dices className="w-4 h-4" />
              </Button>
            </div>
            {password && (
              <div className="mt-2">
                <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className={sInfo.color + " h-full rounded-full transition-all duration-300"} style={{width: strength+"%"}} />
                </div>
                <span className="text-[11px] text-slate-400 mt-1 block">{sInfo.label}</span>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500">{t.notes}</Label>
            <Textarea data-testid="field-notes" value={notes} onChange={function(e){setNotes(e.target.value)}} className="bg-slate-50 border-slate-200 text-slate-800 min-h-[80px]" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-slate-500" data-testid="modal-cancel-button">{t.cancel}</Button>
            <Button type="submit" disabled={saving||!title.trim()||!password.trim()} className="bg-gradient-to-r from-green-700 to-green-800 text-white shadow-sm" data-testid="modal-save-button">
              <Save className="w-4 h-4 mr-1" /> {saving ? t.loading : t.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
